import { computed, inject, Injectable } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { QueryEntity } from '@datorama/akita';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { WorkflowStep } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';

import { QuarterCloseChecklistRowSections } from './workflow-types';
import { WorkflowState, WorkflowStore } from './workflow.store';

@Injectable({ providedIn: 'root' })
export class WorkflowQuery extends QueryEntity<WorkflowState> {
  private launchDarklyService = inject(LaunchDarklyService);

  selectWorkflowList = toSignal(this.selectAll(), { requireSync: true });

  isQuarterCloseEnabled = this.launchDarklyService.$select(
    (flags) => flags.client_preference_quarter_close_workflow_enabled
  );

  isWorkflowAvailable = computed(() => {
    return this.isQuarterCloseEnabled();
  });
  getMonthCloseButtonAvailability = computed(() => {
    const isQuarterCloseEnabled = this.isQuarterCloseEnabled();
    const isWorkflowAvailable = this.isWorkflowAvailable();
    const workflowList = this.selectWorkflowList();

    if (!isQuarterCloseEnabled) {
      return true;
    }

    return isWorkflowAvailable
      ? workflowList
          .filter(
            (workflow) => workflow.section !== QuarterCloseChecklistRowSections['Close Month']
          )
          .every(({ properties: { locked } }) => locked)
      : isWorkflowAvailable;
  });
  prepaidLockTooltip = computed(() => {
    const isInvoiceFinalized = this.getLockStatusByWorkflowStepType(
      WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES
    )();

    return isInvoiceFinalized
      ? 'Locked for Period Close. Navigate to Checklist and unlock in order to make changes.'
      : '';
  });

  isQuarterCloseEnabled$ = toObservable(this.isQuarterCloseEnabled);
  isWorkflowAvailable$ = toObservable(this.isWorkflowAvailable);
  protected store: WorkflowStore;

  constructor() {
    const store = inject(WorkflowStore);

    super(store);

    this.store = store;
  }

  // todo convert to signal later
  getInMonthAdjustmentAvailability$() {
    return this.selectAll().pipe(
      map((workflowList) => {
        return workflowList
          .filter(
            ({ step_type }) => step_type !== WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
          )
          .every(({ properties: { locked } }) => locked);
      })
    );
  }

  getLockStatusByWorkflowStepType(stepType: string) {
    return computed(() => {
      const isWorkflowPanelAvailable = this.isWorkflowAvailable();

      if (!isWorkflowPanelAvailable) {
        return false;
      }

      const workflow = this.getWorkflowByStepType(stepType)();

      return workflow ? workflow.properties.locked : false;
    });
  }

  /**
   * @deprecated use `getLockStatusByWorkflowStepType` instead
   */
  getLockStatusByWorkflowStepType$(stepType: string): Observable<boolean> {
    return combineLatest([this.isWorkflowAvailable$, this.getWorkflowByStepType$(stepType)]).pipe(
      map(([isWorkflowPanelAvailable, workflow]) => {
        if (!isWorkflowPanelAvailable) {
          return false;
        }

        return workflow ? workflow.properties.locked : false;
      })
    );
  }

  getWorkflowByStepType(stepType: string) {
    return computed(() => {
      const list = this.selectWorkflowList().filter((workFlow) => workFlow.step_type === stepType);

      return list.length ? list[0] : null;
    });
  }

  /**
   * @deprecated use `getWorkflowByStepType` instead
   */
  getWorkflowByStepType$(stepType: string) {
    return this.selectAll({
      filterBy: [(workFlow) => workFlow.step_type === stepType],
    }).pipe(map((workflowList) => (workflowList.length ? workflowList[0] : null)));
  }
}
